'use client';
import { getVariantFromSimpleControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import {
    getReviewSourceAlt,
    normalizeReviewSourceIcon,
} from '@/utils/reviewSourceAlt';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'none',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'none';
}

function toBoolean(value, fallback = false) {
    if (value == null) return fallback;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
    }
    return Boolean(value);
}

const ICON_SIZE_CLASSES = {
    default: 'text-xs md:text-sm lg:text-lg',
    'x-small': 'text-xs md:text-sm lg:text-lg',
    small: 'text-sm md:text-base lg:text-xl',
    medium: 'text-base md:text-lg lg:text-2xl',
    large: 'text-lg md:text-2xl lg:text-3xl',
    'x-large': 'text-xl md:text-3xl lg:text-4xl',
};

const getIconSizeClass = (value) =>
    ICON_SIZE_CLASSES[
        String(value || 'default')
            .toLowerCase()
            .trim()
    ] || ICON_SIZE_CLASSES.default;

export default function ReviewFloat(props) {
    const {
        Icon,
        IconSize = 'default',
        Reviews,
        ColorScheme = 'primary',
        Shadow = true,
        Alignment = 'true',
        Animation = 'none',
        AnimationGroup = 'all',
        Duration = 0.6,
        EditorPreview = false,
        is_edit_mode = false,
        EditorControls = null,
    } = props;
    const isEditorMode = EditorPreview || toBoolean(is_edit_mode, false);

    const duration =
        typeof Duration === 'number'
            ? Math.max(0.2, Duration)
            : Math.max(0.2, Number(Duration) || 0.6);
    const preset = normalizeAnimation(Animation);
    const isStagger = preset === 'stagger';

    const rawGroup = String(AnimationGroup || '')
        .toLowerCase()
        .trim();
    const groupMap = {
        content: 'content',
        icons: 'icons',
        stars: 'icons',
        card: 'card',
        all: 'all',
    };
    const group = groupMap[rawGroup] || 'all';
    const animateCard =
        group === 'card' ||
        group === 'content' ||
        group === 'icons' ||
        group === 'all';

    const baseKey = `reviewfloat-${preset}-${duration}-${group}-${
        isEditorMode ? 'preview' : 'live'
    }`;

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (e) => setPrefersReduced(e.matches);
        setPrefersReduced(mq.matches);
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };

    const elementVariants =
        enableAnim && preset !== 'none'
            ? getVariantFromSimpleControls(
                  isStagger ? 'fade' : preset,
                  duration
              )
            : null;

    const buildMotionProps = (variants, { useViewport = true } = {}) => {
        if (!enableAnim || !variants) return {};
        if (!useViewport) return { variants };
        const hasHiddenShow = 'hidden' in variants || 'show' in variants;
        const initial = hasHiddenShow ? 'hidden' : 'offscreen';
        const target = hasHiddenShow ? 'show' : 'onscreen';
        return isEditorMode
            ? { variants, initial, animate: target }
            : { variants, initial, whileInView: target, viewport };
    };

    const review = Array.isArray(Reviews)
        ? Reviews[0]
        : Reviews && typeof Reviews === 'object'
        ? Reviews
        : null;
    if (!review) return null;

    const colorSchemeKey = String(ColorScheme || '').trim() || 'white';
    const {
        backgroundColor = 'bg-white',
        textColor = 'text-neutral-800',
        iconColor: schemeIconColor = 'text-primary-700',
        backgroundStyle,
    } = getColorScheme(colorSchemeKey, props.GradientDirection);

    const outlineBorders = {
        outline: 'border border-neutral-200',
        'outline-primary': 'border border-primary-700',
        'outline-secondary': 'border border-secondary-700',
    };
    const outlineIcons = {
        outline: 'text-primary-700',
        'outline-primary': 'text-primary-800',
        'outline-secondary': 'text-secondary-800',
    };

    const backgroundClass = [backgroundColor, outlineBorders[colorSchemeKey]]
        .filter(Boolean)
        .join(' ');
    const iconColor =
        outlineIcons[colorSchemeKey] || schemeIconColor || 'text-primary-700';

    // Define alignment classes
    let containerAlign, hSpace;
    switch (Alignment) {
        case 'left':
            containerAlign = 'mx-auto lg:mx-0';
            hSpace = 'lg:pl-8 xl:pl-6';
            break;
        case 'right':
            containerAlign = 'mx-auto lg:mx-0 lg:ml-auto';
            hSpace = 'lg:pr-8  xl:pr-6';
            break;
        default: // center
            containerAlign = 'justify-center mx-auto';
            hSpace = '';
            break;
    }

    const shadowBox = toBoolean(Shadow, true) ? 'shadow-xl' : '';
    const iconSizeClass = getIconSizeClass(IconSize);
    const cardMotionProps =
        enableAnim && animateCard ? buildMotionProps(elementVariants) : {};
    const IconComponent = 'i';
    const sectionClass = isEditorMode
        ? `editable-component pointer-events-none absolute top-0 z-40 w-full -translate-y-1/2 ${hSpace}`
        : `editable-component pointer-events-none absolute z-40 w-full ${hSpace}`;
    const wrapperClass = isEditorMode
        ? 'm-w- pointer-events-none mx-auto flex max-w-screen-2xl'
        : 'm-w- pointer-events-none mx-auto mt-[-90px] flex max-w-screen-2xl md:mt-[-100px] lg:mt-[-135px]';

    return (
        <section className={sectionClass} data-json="reviewfloat">
            <MotionMaybe
                enable={enableAnim && animateCard}
                k={`${baseKey}-wrapper`}
                motionProps={cardMotionProps}
                className={wrapperClass}
            >
                <div
                    className={`group/reviewfloat-editor pointer-events-auto relative max-w-xs md:max-w-lg lg:max-w-xl ${containerAlign}`}
                >
                    {EditorControls}
                    <div
                        className={`relative z-10 ${backgroundClass} ${shadowBox} flex w-full rounded-[min(1.5rem,var(--shape-radius))] px-3 py-5 md:px-4 md:py-7 lg:px-6 lg:py-10`}
                        style={backgroundStyle}
                    >
                        <div className="relative mx-auto max-w-lg">
                            <div className="text-center"></div>
                            <div className="flex justify-center">
                                {[0, 1, 2, 3, 4].map((idx) => (
                                    <IconComponent
                                        key={`review-icon-${idx}-${baseKey}`}
                                        className={`${Icon} mx-1 ${iconColor} ${iconSizeClass}`}
                                    />
                                ))}
                            </div>
                            {/*Bold Review*/}
                            <figure className="mt-3 items-center justify-center text-pretty md:mt-4 lg:mt-6">
                                <blockquote
                                    className={`${textColor} text-center text-sm leading-5 md:text-base md:leading-6 lg:text-lg`}
                                >
                                    <MarkDown>
                                        {'“' + truncate(review.Body) + '”'}
                                    </MarkDown>
                                </blockquote>

                                <figcaption className="mt-2 md:mt-4 lg:mt-5">
                                    <div className="mt-4 flex items-center justify-center space-x-3 text-xs  md:text-sm lg:text-base">
                                        <img
                                            className="-ml-8 h-5 w-5 rounded-full bg-neutral-50 p-1 ring-2 ring-white md:h-6 md:w-6 lg:-ml-9"
                                            src={normalizeReviewSourceIcon(
                                                review.Author.Icon
                                            )}
                                            alt={getReviewSourceAlt(
                                                review.Author.Icon
                                            )}
                                        />
                                        <MarkDown
                                            wrapperClassName="inline"
                                            className={`${textColor} inline`}
                                            components={{
                                                p: ({ children, ...rest }) => (
                                                    <span
                                                        {...rest}
                                                        className={`${textColor}`}
                                                    >
                                                        {children}
                                                    </span>
                                                ),
                                            }}
                                        >
                                            {review.Author.Name}
                                        </MarkDown>
                                    </div>
                                </figcaption>
                            </figure>
                        </div>
                    </div>
                </div>
            </MotionMaybe>
        </section>
    );
}
function truncate(text, maxLength = 125) {
    if (text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
    }
    return text;
}
